import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { AppContext } from '@umss/shared/core';
import {
  AuthService,
  CorporateService,
  CorporationDto
} from '@umss/shared/data-access';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom
} from 'rxjs';

@Injectable({ providedIn: 'root' })
export class CorporateContextService implements AppContext {
  //private readonly MOCK_FACILITY_ID = '650030a4-9a7d-4813-aad5-5cb8e6b39964';

  private authService = inject(AuthService);
  private corporateService = inject(CorporateService);

  private readonly INITIAL_VALUE = '';

  /**
   * Corporate id that is computed based on the auth state.
   * If you account does not have a CorporateId, then replace the this.INITIAL_VALUE by
   * the MOCK_FACILITY_ID but only for testing purposes.
   */
  corporateId = computed(() => {
    return this.authService.authState().corporateId || this.INITIAL_VALUE;
  });

  /**
   * Flag used by the AuthRefresh provider to check if the CorporateContext has loaded
   * It's mark as ready when it is different from the initial value
   */
  readonly ready = computed(
    () => this.corporateId().trim() !== this.INITIAL_VALUE
  );

  /**
   * Corporate object of the logged-in user.
   * This is automatically loaded when the corporateId changes.
   */
  private _corporate = signal<CorporationDto | null>(null);
  readonly corporate = this._corporate.asReadonly();

  /**
   * Loading state for the corporate data
   */
  private _loadingCorporate = signal<boolean>(false);
  readonly loadingCorporate = this._loadingCorporate.asReadonly();

  // Shared in-flight promise so bootstrap (ensureLoaded) and the in-session
  // login subscription deduplicate when both fire for the same corporateId.
  private _loadInFlight: Promise<void> | null = null;
  private _loadedId: string | null = null;

  constructor() {
    // Handles in-session auth changes (e.g. AuthService.login / socialLogin
    // after the app is already running). Bootstrap is handled by ensureLoaded().
    const corporateId$ = toObservable(this.corporateId).pipe(
      filter((id) => !!id && id !== this.INITIAL_VALUE),
      distinctUntilChanged()
    );

    const hasToken$ = toObservable(
      computed(() => !!this.authService.accessToken)
    ).pipe(filter(Boolean));

    combineLatest([corporateId$, hasToken$])
      .pipe(
        takeUntilDestroyed(),
        distinctUntilChanged(([prevId], [currId]) => prevId === currId)
      )
      .subscribe(([id]) => this.loadCorporate(id));
  }

  /**
   * Synchronously inspects the auth state and triggers the corporate fetch if a
   * corporateId is present. Used by the bootstrap to avoid the
   * signal → toObservable → effect-flush race during `provideAppInitializer`.
   */
  ensureLoaded(): Promise<void> {
    const id = this.corporateId();
    if (!id) return Promise.resolve();
    if (this._loadedId === id && this._corporate()) return Promise.resolve();
    return this.loadCorporate(id);
  }

  private loadCorporate(id: string): Promise<void> {
    if (this._loadInFlight) return this._loadInFlight;

    this._loadingCorporate.set(true);

    this._loadInFlight = firstValueFrom(this.corporateService.get(id))
      .then((corporate) => {
        this._corporate.set(corporate);
        this._loadedId = id;
      })
      .catch(() => {
        this._corporate.set(null);
        this._loadedId = null;
      })
      .finally(() => {
        this._loadingCorporate.set(false);
        this._loadInFlight = null;
      });

    return this._loadInFlight;
  }
}
